import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { 
  Search, 
  ShoppingCart, 
  Heart, 
  Package, 
  User,
  Zap,
  Sun,
  Moon,
  Monitor
} from "lucide-react";
import { hapticFeedback } from "@/utils/mobileUtils";
import { useTheme } from "@/contexts/ThemeContext";

/**
 * MobileMenuQuickActions Component
 * 
 * Displays a grid of quick action buttons for common user tasks.
 * Phase 1.2: Enhanced with vibrant gradients, larger touch targets, and improved animations
 * Phase 1.3: Theme toggle with animated icon transitions
 * 
 * Features:
 * - Larger touch targets (110px min height) for better accessibility
 * - Vibrant color-coded action buttons
 * - Animated icon interactions (wiggle on hover)
 * - Cart badge with item count
 * - Auth-gated actions (wishlist, orders)
 * - Prominent theme toggle with visual feedback
 * - Spring animations for playful feel
 * 
 * @param {MobileMenuQuickActionsProps} props - Component props
 * @returns {JSX.Element} Quick actions grid
 */

interface AuthUser {
  email?: string | null;
  role?: string;
}

interface MobileMenuQuickActionsProps {
  user?: AuthUser;
  itemCount: number;
  handleLinkClick: (path: string) => void;
}

const quickActions = [
  { 
    name: 'Search', 
    href: '/search', 
    icon: Search, 
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-gradient-to-br from-blue-500/20 dark:from-blue-500/15 to-blue-600/10 hover:from-blue-500/30 dark:hover:from-blue-500/25 hover:to-blue-600/20',
    borderColor: 'border-blue-500/40 dark:border-blue-500/30 hover:border-blue-500/60 dark:hover:border-blue-500/50',
    description: 'Find products'
  },
  { 
    name: 'Cart', 
    href: '/cart', 
    icon: ShoppingCart, 
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-gradient-to-br from-emerald-500/20 dark:from-emerald-500/15 to-emerald-600/10 hover:from-emerald-500/30 dark:hover:from-emerald-500/25 hover:to-emerald-600/20',
    borderColor: 'border-emerald-500/40 dark:border-emerald-500/30 hover:border-emerald-500/60 dark:hover:border-emerald-500/50',
    description: 'View cart',
    showBadge: true
  },
  { 
    name: 'Wishlist', 
    href: '/wishlist', 
    icon: Heart, 
    color: 'text-rose-600 dark:text-rose-400',
    bgColor: 'bg-gradient-to-br from-rose-500/20 dark:from-rose-500/15 to-rose-600/10 hover:from-rose-500/30 dark:hover:from-rose-500/25 hover:to-rose-600/20',
    borderColor: 'border-rose-500/40 dark:border-rose-500/30 hover:border-rose-500/60 dark:hover:border-rose-500/50',
    description: 'Saved items',
    requiresAuth: true
  },
  { 
    name: 'Orders', 
    href: '/orders', 
    icon: Package, 
    color: 'text-violet-600 dark:text-violet-400',
    bgColor: 'bg-gradient-to-br from-violet-500/20 dark:from-violet-500/15 to-violet-600/10 hover:from-violet-500/30 dark:hover:from-violet-500/25 hover:to-violet-600/20',
    borderColor: 'border-violet-500/40 dark:border-violet-500/30 hover:border-violet-500/60 dark:hover:border-violet-500/50',
    description: 'Track orders',
    requiresAuth: true
  }
];

const itemVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25
    }
  },
};

const MobileMenuQuickActions = ({ user, itemCount, handleLinkClick }: MobileMenuQuickActionsProps) => {
  const { theme, effectiveTheme, toggleTheme } = useTheme();
  
  const handleActionClick = (href: string, requiresAuth?: boolean) => {
    hapticFeedback('medium');
    
    if (requiresAuth && !user) {
      handleLinkClick('/auth');
      return;
    }
    
    handleLinkClick(href);
  };

  const visibleActions = quickActions.filter(action => 
    !action.requiresAuth || user
  );

  const getThemeIcon = () => {
    if (theme === 'system') return Monitor;
    return effectiveTheme === 'dark' ? Sun : Moon;
  };

  const getThemeLabel = () => {
    if (theme === 'system') return 'System';
    return effectiveTheme === 'dark' ? 'Dark' : 'Light';
  };

  const handleThemeToggle = () => {
    hapticFeedback('medium');
    toggleTheme();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-1">
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
        >
          <Zap className="h-5 w-5 text-primary" />
        </motion.div>
        <h3 className="text-sm font-bold text-foreground">Quick Actions</h3>
        <div className="flex-1 h-px bg-gradient-to-r from-border/20 to-transparent" />
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {visibleActions.map((action, index) => {
          const Icon = action.icon;
          return (
            <motion.div 
              key={action.name}
              variants={itemVariants}
              custom={index}
            >
              <motion.button
                onClick={() => handleActionClick(action.href, action.requiresAuth)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className={`
                  relative w-full min-h-[90px] p-4 rounded-2xl border ${action.borderColor} ${action.bgColor}
                  transition-all duration-300 
                  shadow-sm hover:shadow-lg
                  focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2
                  group active:shadow-xl
                `}
              >
                <div className="flex flex-col items-center text-center space-y-2">
                  <motion.div 
                    className="relative"
                    whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="p-2 rounded-xl bg-background/60 dark:bg-background/40 backdrop-blur-sm">
                      <Icon className={`h-7 w-7 ${action.color} transition-all duration-200`} />
                    </div>
                    {action.showBadge && itemCount > 0 && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 15 }}
                      >
                        <Badge 
                          variant="destructive" 
                          className="absolute -top-1 -right-1 h-6 w-6 p-0 flex items-center justify-center text-[11px] font-bold shadow-lg border-2 border-background"
                        >
                          {itemCount > 99 ? '99+' : itemCount}
                        </Badge>
                      </motion.div>
                    )}
                  </motion.div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-0.5">
                      {action.name}
                    </p>
                    <p className="text-[10px] text-muted-foreground/90 dark:text-muted-foreground/80">
                      {action.description}
                    </p>
                  </div>
                </div>
              </motion.button>
            </motion.div>
          );
        })}
      </div>
      
      {/* Theme Toggle */}
      <motion.div variants={itemVariants}>
        <motion.button
          onClick={handleThemeToggle}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
          className="w-full p-3 rounded-2xl border border-primary/40 dark:border-primary/30 bg-gradient-to-br from-primary/15 dark:from-primary/10 via-accent/10 to-secondary/10 hover:from-primary/20 dark:hover:from-primary/15 hover:via-accent/15 hover:to-secondary/15 transition-all duration-300 hover:border-primary/60 dark:hover:border-primary/50 hover:shadow-lg group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <motion.div
                key={theme}
                initial={{ rotate: -180, scale: 0 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="p-2 rounded-xl bg-primary/20 backdrop-blur-sm"
              >
                {(() => {
                  const ThemeIcon = getThemeIcon();
                  return <ThemeIcon className="h-5 w-5 text-primary" />;
                })()}
              </motion.div>
              <div className="text-left">
                <span className="text-sm font-semibold text-foreground block">
                  {getThemeLabel()} Mode
                </span>
                <span className="text-[10px] text-muted-foreground/90 dark:text-muted-foreground/80">
                  Tap to switch
                </span>
              </div>
            </div>
            <div className="text-xs font-medium text-primary bg-primary/15 px-3 py-1.5 rounded-lg border border-primary/20">
              Change
            </div>
          </div>
        </motion.button>
      </motion.div>
      
      {!user && (
        <motion.div variants={itemVariants}>
          <motion.button
            onClick={() => handleActionClick('/auth')}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            className="w-full p-3 rounded-2xl border border-primary/40 bg-gradient-to-r from-primary/10 to-secondary/10 hover:from-primary/20 hover:to-secondary/20 transition-all duration-300 hover:border-primary/60 hover:shadow-lg group"
          >
            <div className="flex items-center justify-center space-x-3">
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
                className="p-2 rounded-xl bg-primary/20"
              >
                <User className="h-5 w-5 text-primary" />
              </motion.div>
              <span className="text-sm font-semibold text-primary">Sign In for More Features</span>
            </div>
          </motion.button>
        </motion.div>
      )}
    </div>
  );
};

export default MobileMenuQuickActions;